import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Users, Globe, Mail, FileText } from 'lucide-react';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      {/* Header */}
      <header className="border-b border-secondary-200 dark:border-secondary-800">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <h1 className="text-xl font-bold text-secondary-900 dark:text-white">Privacy Policy</h1>
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              Last Updated: August 2026
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="prose prose-sm prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <div className="bg-secondary-100 dark:bg-secondary-900 rounded-lg p-6 mb-8">
              <div className="flex items-start gap-4">
                <Shield className="h-5 w-5 text-secondary-500 mt-1" />
                <div>
                  <h2 className="text-base font-semibold mb-2 text-secondary-900 dark:text-white">Your Privacy Matters</h2>
                  <p className="text-secondary-700 dark:text-secondary-300">
                    At Wisetree AI, operated by Wisetree AI Pty Ltd, we are committed to protecting your privacy and ensuring the security of your data.
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our storytelling studio —
                    where you create media works with an AI production crew, and, where available, buy and sell those works on our marketplace.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
              <Eye className="h-5 w-5 text-secondary-500" />
              Information We Collect
            </h2>

            <div className="space-y-6">
              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Account Information</h3>
                <ul className="space-y-2 text-secondary-700 dark:text-secondary-300">
                  <li>• Email address and name from your Google account</li>
                  <li>• Profile picture (if available)</li>
                  <li>• Authentication tokens for secure login</li>
                </ul>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Creation Content</h3>
                <ul className="space-y-2 text-secondary-700 dark:text-secondary-300">
                  <li>• Your prompts, briefs, story ideas, and chat messages with the crew</li>
                  <li>• Files you upload and the media works you generate (video, images, audio)</li>
                  <li>• Project structure — beat sheets, shot lists, character locks, and edits</li>
                </ul>
                <p className="mt-3 text-sm text-secondary-500">
                  Your prompts and generated works are sent to the AI providers that power generation solely to produce your output. We do not use your
                  private content to train our own models, and your works stay private to you unless you choose to share or list them.
                </p>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Credits, Payments &amp; Marketplace</h3>
                <ul className="space-y-2 text-secondary-700 dark:text-secondary-300">
                  <li>• Credit balance and usage history (what you created and what it cost)</li>
                  <li>• Purchase and subscription records (handled by our payment provider — we do not store your card details)</li>
                  <li>• Marketplace activity — listings you publish, works you buy, and sales</li>
                  <li>• Payout details you provide to receive seller earnings, verified through our payments partner</li>
                </ul>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Usage Information</h3>
                <ul className="space-y-2 text-secondary-700 dark:text-secondary-300">
                  <li>• Features you use and actions you take</li>
                  <li>• Performance metrics and error reports</li>
                  <li>• Browser type and device information</li>
                  <li>• IP address and general location data</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-secondary-500" />
              How We Use Your Information
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Service Delivery</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  To power your creation flow, generate your media works, run credits and payments, and operate the marketplace.
                </p>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Communication</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  To send important updates, security alerts, and respond to your support requests.
                </p>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Security</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  To detect, prevent, and address technical issues, fraud, and unauthorized access.
                </p>
              </div>

              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">Analytics</h3>
                <p className="text-secondary-700 dark:text-secondary-300">
                  To understand how users interact with our service and make data-driven improvements.
                </p>
              </div>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-secondary-500" />
              Data Sharing & Disclosure
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following situations:
              </p>
              <ul className="space-y-3 text-secondary-700 dark:text-secondary-300">
                <li className="flex items-start gap-2">
                  <span className="text-secondary-500 mt-1">•</span>
                  <span><strong>With Your Consent:</strong> When you explicitly agree to share information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary-500 mt-1">•</span>
                  <span><strong>AI Providers:</strong> We send your prompts and inputs to the AI providers that generate your works, solely to produce your output</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary-500 mt-1">•</span>
                  <span><strong>Payments &amp; Payouts:</strong> With our payment provider to process purchases, and, if you sell, to verify your identity and pay out earnings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary-500 mt-1">•</span>
                  <span><strong>Marketplace:</strong> If you list a work, its preview and the details you choose to publish are shown to other users, and the delivered work is provided to buyers you sell to</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary-500 mt-1">•</span>
                  <span><strong>Service Providers:</strong> With trusted third-party services that help us operate (e.g., Google Firebase and Cloud)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-secondary-500 mt-1">•</span>
                  <span><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-secondary-500" />
              Data Security
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                We implement industry-standard security measures to protect your data:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 bg-secondary-500 rounded-full"></div>
                  <span className="text-secondary-700 dark:text-secondary-300">Encryption in transit and at rest</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 bg-secondary-500 rounded-full"></div>
                  <span className="text-secondary-700 dark:text-secondary-300">Regular security audits</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 bg-secondary-500 rounded-full"></div>
                  <span className="text-secondary-700 dark:text-secondary-300">Access controls and authentication</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 bg-secondary-500 rounded-full"></div>
                  <span className="text-secondary-700 dark:text-secondary-300">Secure data centers</span>
                </div>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-secondary-500" />
              Your Rights & Choices
            </h2>

            <div className="space-y-4">
              <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
                <h3 className="text-base font-semibold mb-3 text-secondary-900 dark:text-white">You have the right to:</h3>
                <ul className="space-y-2 text-secondary-700 dark:text-secondary-300">
                  <li>• Access and receive a copy of your personal data</li>
                  <li>• Correct or update inaccurate information</li>
                  <li>• Delete your account and associated data</li>
                  <li>• Export your data in a portable format</li>
                  <li>• Opt-out of non-essential communications</li>
                  <li>• Restrict processing of your data in certain circumstances</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
              <Globe className="h-5 w-5 text-secondary-500" />
              Data Retention
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                We retain your information for as long as your account is active or as needed to provide you services.
                If you delete your account, we will delete your personal information within 30 days, except where we are
                required to retain it for legal obligations, dispute resolution, or enforcement of our agreements.
              </p>
            </div>
          </section>

          {/* International Transfers */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white">International Data Transfers</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                Your information may be transferred to and maintained on servers located outside of your state, province,
                country, or other governmental jurisdiction. We ensure appropriate safeguards are in place to protect your
                data in accordance with this Privacy Policy.
              </p>
            </div>
          </section>

          {/* Children's Privacy */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white">Children's Privacy</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                Wisetree AI is not intended for use by children under the age of 13. We do not knowingly collect personal
                information from children under 13. If you believe we have collected information from a child under 13,
                please contact us immediately.
              </p>
            </div>
          </section>

          {/* Updates to Policy */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white">Updates to This Policy</h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
                Privacy Policy on this page and updating the "Last Updated" date. For major changes, we will provide
                additional notice via email or through the service.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-10">
            <h2 className="text-lg font-bold mb-4 text-secondary-900 dark:text-white flex items-center gap-2">
              <Mail className="h-5 w-5 text-secondary-500" />
              Contact Us
            </h2>

            <div className="bg-secondary-100 dark:bg-secondary-900/50 rounded-lg p-6">
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-secondary-700 dark:text-secondary-300">
                <p><strong>Wisetree AI Pty Ltd</strong></p>
                <p><strong>Email:</strong> support@wisetree.ai</p>
                <p className="mt-4 text-sm text-secondary-500">
                  <strong>SOC 2 Compliant Infrastructure:</strong> Wisetree AI is hosted on Google Cloud Platform with SOC 2 Type II certification,
                  ensuring enterprise-grade security and data protection.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-secondary-200 dark:border-secondary-800 mt-20">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              © 2026 Wisetree AI Pty Ltd. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/terms" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/dashboard" className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white transition-colors">
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;